import { useState } from "react";
import { uploadBeachData } from "../services/api";
import { getErrorAlertMessage } from "../utils/errors";

export function BeachUpload() {
  const [csvContent, setCsvContent] = useState("");
  const [weekDate, setWeekDate] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{
    type: "success" | "error";
    message: string;
    details?: {
      records_created?: number;
      skill_indexes_created?: number;
      total_records_attempted?: number;
      filtered_count?: number;
      total_rows_in_csv?: number;
      errors?: string[];
    };
  } | null>(null);

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) {
      setCsvContent("");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvContent(text);
    };
    reader.onerror = () => {
      setResult({
        type: "error",
        message: "Failed to read file",
      });
    };
    reader.readAsText(file);
  }

  async function handleUpload() {
    if (!csvContent.trim()) {
      setResult({
        type: "error",
        message: "Please select a CSV file first",
      });
      return;
    }

    if (!weekDate) {
      setResult({
        type: "error",
        message: "Please select a week date",
      });
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const response = await uploadBeachData(csvContent, weekDate);

      if (response.success) {
        setResult({
          type: "success",
          message: "Beach data uploaded successfully!",
          details: {
            records_created: response.records_created,
            skill_indexes_created: response.skill_indexes_created,
            total_records_attempted: response.total_records_attempted,
            filtered_count: response.filtered_count,
            total_rows_in_csv: response.total_rows_in_csv,
            errors: response.errors,
          },
        });
        setCsvContent("");
      } else {
        setResult({
          type: "error",
          message: "Upload completed with errors",
          details: {
            records_created: response.records_created,
            skill_indexes_created: response.skill_indexes_created,
            total_records_attempted: response.total_records_attempted,
            filtered_count: response.filtered_count,
            total_rows_in_csv: response.total_rows_in_csv,
            errors: response.errors,
          },
        });
      }
    } catch (err) {
      setResult({
        type: "error",
        message: getErrorAlertMessage("upload beach data", err),
      });
    } finally {
      setUploading(false);
    }
  }

  function handleReset() {
    setCsvContent("");
    setResult(null);
  }

  return (
    <div className="layout single-column">
      <div className="panel">
        <h2>Beach Data Upload</h2>
        <p className="helper-text">
          Upload weekly beach data CSV with 25 columns: employee ID, employee name, Weeks in DATE, staffing office, etc.
        </p>

        <div className="form-grid">
          <label className="span-2">
            CSV File
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              disabled={uploading}
            />
          </label>

          <label>
            Week Date
            <input
              type="date"
              value={weekDate}
              onChange={(e) => setWeekDate(e.target.value)}
              disabled={uploading}
            />
            <small className="helper-text">All records in this CSV will be assigned to this week (ignores dates in CSV file)</small>
          </label>

          <div className="span-2">
            {csvContent && (
              <div className="alert info">
                <p>CSV loaded: {csvContent.split('\n').length - 1} rows (excluding header)</p>
              </div>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="primary"
            onClick={handleUpload}
            disabled={uploading || !csvContent.trim()}
          >
            {uploading ? "Uploading..." : "Upload Beach Data"}
          </button>
          <button
            type="button"
            className="ghost"
            onClick={handleReset}
            disabled={uploading}
          >
            Reset
          </button>
        </div>
      </div>

      {result && (
        <div className={`panel ${result.type === "success" ? "success" : "error"}`}>
          <h3>{result.type === "success" ? "✓ Success" : "⚠ Error"}</h3>
          <p>{result.message}</p>

          {result.details && (
            <>
              <div className="detail-block">
                <h4>Upload Summary</h4>
                <ul>
                  <li>Total rows in CSV: {result.details.total_rows_in_csv}</li>
                  <li>Records filtered out: {result.details.filtered_count}</li>
                  <li>Records created: {result.details.records_created}</li>
                  <li>Skill indexes created: {result.details.skill_indexes_created}</li>
                </ul>
                <p className="helper-text" style={{ marginTop: "0.5rem" }}>
                  Filtered records excluded based on: grade (Associate, Director, Distinguished, Intern, SVP, VP),
                  partially available, leaving beach, has long leave, has future assignment, or has tentative assignment.
                </p>
              </div>

              {result.details.errors && result.details.errors.length > 0 && (
                <div className="detail-block">
                  <h4>Errors ({result.details.errors.length})</h4>
                  <ul>
                    {result.details.errors.slice(0, 10).map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                    {result.details.errors.length > 10 && (
                      <li>...and {result.details.errors.length - 10} more errors</li>
                    )}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
